import React, { useState } from "react";
import { motion } from "framer-motion";
import {
	Info,
	ChevronDown,
	ChevronUp,
	CheckCircle,
	XCircle,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Badge } from "../GraderUI";
import { ProviderIcon } from "../shared/ProviderIcon";

const PROVIDER_LABELS = {
	openai: "OpenAI",
	gemini: "Google Gemini",
	claude: "Anthropic Claude",
};

const SUB_SCORE_LABELS = {
	content_quality: "Content quality",
	entity_recognition: "Entity recognition",
	answer_format: "Answer format",
	citation_readiness: "Citability potential",
};

const CITABILITY_TOOLTIP =
	"Theoretical score based on content signals such as schema, entity clarity and structured data. Actual citation rate is measured separately by testing live AI responses.";

const providerConfig = {
	openai: {
		name: "OpenAI GPT-4",
		brandColor: "#10a37f",
	},
	gemini: {
		name: "Google Gemini",
		brandColor: "#4285f4",
	},
	claude: {
		name: "Anthropic Claude",
		brandColor: "#c96442",
	},
};

const getScoreBadge = (score) => {
	if (score >= 80) return { label: "Strong", variant: "success" };
	if (score >= 60) return { label: "Moderate", variant: "warning" };
	return { label: "Needs work", variant: "destructive" };
};

const getBarFillClass = (score) => {
	if (score >= 70) return "provider-metrics__subscore-fill--high";
	if (score >= 50) return "provider-metrics__subscore-fill--medium";
	return "provider-metrics__subscore-fill--low";
};

function SubScoreBar({ label, score, showTooltip, tooltipText }) {
	const roundedScore = Math.round(score);
	const displayScore = score > 0 ? roundedScore : "—";

	return (
		<div className="provider-metrics__subscore">
			<div className="provider-metrics__subscore-label-wrap">
				{showTooltip && tooltipText ? (
					<span className="provider-metrics__subscore-label provider-metrics__subscore-label--tooltip" title={tooltipText}>
						{label}
					</span>
				) : (
					<span className="provider-metrics__subscore-label">{label}</span>
				)}
			</div>
			<div className="provider-metrics__subscore-track">
				<motion.div
					className={`provider-metrics__subscore-fill ${getBarFillClass(score)}`}
					initial={{ width: 0 }}
					animate={{ width: `${score}%` }}
					transition={{ duration: 0.4, ease: "easeOut" }}
				/>
			</div>
			<div className="provider-metrics__subscore-value">{displayScore}</div>
		</div>
	);
}

function QueryResultRow({ queryResult }) {
	const [isExpanded, setIsExpanded] = useState(false);

	const sanitizedResponse = queryResult.response.replace(/<[^>]*>/g, "");
	const needsTruncation = sanitizedResponse.length > 200;
	const displayResponse =
		needsTruncation && !isExpanded
			? `${sanitizedResponse.slice(0, 200)}...`
			: sanitizedResponse;

	const maxQueryLength = 80;
	const displayQuery =
		queryResult.query.length > maxQueryLength
			? `${queryResult.query.slice(0, maxQueryLength)}...`
			: queryResult.query;

	return (
		<div className="provider-metrics__query">
			<div className="provider-metrics__query-header">
				<div className="provider-metrics__query-question">
					<span className="provider-metrics__query-badge">Q</span>
					<span className="provider-metrics__query-text" title={queryResult.query}>
						{displayQuery}
					</span>
				</div>
				<div className="provider-metrics__query-meta">
					<Badge variant="outline" className="provider-metrics__query-score">
						{Math.round(queryResult.score)}
					</Badge>
					{queryResult.pass ? (
						<CheckCircle className="provider-metrics__query-icon provider-metrics__query-icon--pass" />
					) : (
						<XCircle className="provider-metrics__query-icon provider-metrics__query-icon--fail" />
					)}
				</div>
			</div>

			<div className="provider-metrics__query-response">
				<div className="provider-metrics__markdown">
					<ReactMarkdown
						components={{
							p: ({ children }) => <p className="provider-metrics__markdown-p">{children}</p>,
							a: ({ href, children }) => (
								<a
									href={href}
									target="_blank"
									rel="noopener noreferrer"
									className="provider-metrics__markdown-link"
								>
									{children}
								</a>
							),
							strong: ({ children }) => (
								<strong className="provider-metrics__markdown-strong">{children}</strong>
							),
							em: ({ children }) => <em className="provider-metrics__markdown-em">{children}</em>,
							ul: ({ children }) => (
								<ul className="provider-metrics__markdown-ul">{children}</ul>
							),
							ol: ({ children }) => (
								<ol className="provider-metrics__markdown-ol">{children}</ol>
							),
							li: ({ children }) => (
								<li className="provider-metrics__markdown-li">{children}</li>
							),
							code: ({ children }) => (
								<code className="provider-metrics__markdown-code">{children}</code>
							),
							blockquote: ({ children }) => (
								<blockquote className="provider-metrics__markdown-blockquote">{children}</blockquote>
							),
						}}
					>
						{displayResponse}
					</ReactMarkdown>
				</div>
				{needsTruncation && (
					<button
						type="button"
						className="provider-metrics__query-toggle"
						onClick={() => setIsExpanded(!isExpanded)}
					>
						{isExpanded ? "Show less" : "Show full response"}
					</button>
				)}
			</div>
		</div>
	);
}

export default function ProviderMetricsSection({ metrics, citationData }) {
	const [allExpanded, setAllExpanded] = useState(false);

	if (!metrics || metrics.length === 0) {
		return (
			<div className="provider-metrics provider-metrics--empty">
				<p className="provider-metrics__empty-text">Provider metrics data not available</p>
			</div>
		);
	}

	const toggleAllProviders = () => {
		setAllExpanded(!allExpanded);
	};

	return (
		<motion.div
			className="provider-metrics"
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
		>
			<div className="provider-metrics__header">
				<div>
					<h2 className="provider-metrics__title">AI provider evaluation</h2>
					<p className="provider-metrics__subtitle">
						Detailed performance analysis from each AI model
					</p>
				</div>
				<button
					type="button"
					className="provider-metrics__info-btn"
					title="Each provider uses different criteria. OpenAI weights structured data. Gemini prioritises entity clarity. Claude focuses on prose quality and factual density."
				>
					<Info className="provider-metrics__info-icon" />
				</button>
			</div>

			<div className="provider-metrics__grid">
				{metrics.map((metric, index) => {
					const config = providerConfig[metric.provider];
					if (!config) return null;
					const scoreBadge = getScoreBadge(metric.aeo_score || 0);
					const score = Math.round(metric.aeo_score || 0);
					const providerCitation = citationData?.per_provider?.[metric.provider];

					return (
						<motion.div
							key={metric.provider}
							className="provider-metrics__card"
							style={{ borderLeftColor: config.brandColor }}
							initial={{ opacity: 0, scale: 0.95 }}
							animate={{ opacity: 1, scale: 1 }}
							transition={{ duration: 0.4, delay: index * 0.1 }}
						>
							<div className="provider-metrics__card-header">
								<div className="provider-metrics__card-title-row">
									<div className="provider-metrics__card-title">
										<ProviderIcon
											provider={metric.provider}
											size={20}
											className="provider-metrics__card-icon"
										/>
										<h3 className="provider-metrics__card-name">
											{PROVIDER_LABELS[metric.provider] ?? metric.provider}
										</h3>
									</div>
									{metric.queries_evaluated && (
										<button
											type="button"
											onClick={toggleAllProviders}
											className="provider-metrics__queries-btn"
										>
											{metric.queries_evaluated} queries tested
											{allExpanded ? (
												<ChevronUp className="provider-metrics__queries-icon" />
											) : (
												<ChevronDown className="provider-metrics__queries-icon" />
											)}
										</button>
									)}
								</div>
								<div className="provider-metrics__score-row">
									<span className="provider-metrics__score">{score}</span>
									<Badge variant={scoreBadge.variant}>{scoreBadge.label}</Badge>
								</div>
								{providerCitation && (
									<div className="provider-metrics__citation-summary">
										<span className="provider-metrics__cited">
											Cited {Math.round(providerCitation.citation_rate * 100)}%
										</span>
										<span className="provider-metrics__citation-sep">·</span>
										<span className="provider-metrics__mentioned">
											Mentioned {Math.round((providerCitation.mention_rate || 0) * 100)}%
										</span>
									</div>
								)}
							</div>

							<div className="provider-metrics__subscores">
								{metric.sub_scores ? (
									<>
										<SubScoreBar
											label={SUB_SCORE_LABELS.content_quality}
											score={metric.sub_scores.content_quality}
										/>
										<SubScoreBar
											label={SUB_SCORE_LABELS.entity_recognition}
											score={metric.sub_scores.entity_recognition}
										/>
										<SubScoreBar
											label={SUB_SCORE_LABELS.answer_format}
											score={metric.sub_scores.answer_format}
										/>
										<SubScoreBar
											label={SUB_SCORE_LABELS.citation_readiness}
											score={metric.sub_scores.citation_readiness}
											showTooltip
											tooltipText={CITABILITY_TOOLTIP}
										/>
									</>
								) : (
									<div className="provider-metrics__no-data">
										<p className="provider-metrics__no-data-title">
											Real AI testing data not available
										</p>
										<p className="provider-metrics__no-data-sub">
											Enhanced provider evaluation requires real-time AI queries
										</p>
									</div>
								)}
							</div>

							{metric.sub_scores && (metric.strength || metric.weakness) && (
								<div className="provider-metrics__insights">
									{metric.strength && (
										<div>
											<span className="provider-metrics__strength-label">Strength: </span>
											<span>{metric.strength}</span>
										</div>
									)}
									{metric.weakness && (
										<div>
											<span className="provider-metrics__gap-label">Gap: </span>
											<span>{metric.weakness}</span>
										</div>
									)}
								</div>
							)}

							{providerCitation && (
								<div className="provider-metrics__discoverability">
									<div className="provider-metrics__discoverability-title">Discoverability</div>
									<div className="provider-metrics__discoverability-rows">
										<div className="provider-metrics__discoverability-row">
											<div className="provider-metrics__discoverability-header">
												<span>
													Cited in {providerCitation.cited_count || 0} /{" "}
													{providerCitation.queries_tested} queries
												</span>
												<span className="provider-metrics__discoverability-pct provider-metrics__discoverability-pct--cited">
													{Math.round(providerCitation.citation_rate * 100)}%
												</span>
											</div>
											<div className="provider-metrics__discoverability-track">
												<div
													className="provider-metrics__discoverability-fill provider-metrics__discoverability-fill--cited"
													style={{
														width: `${Math.round(providerCitation.citation_rate * 100)}%`,
													}}
												/>
											</div>
										</div>

										<div className="provider-metrics__discoverability-row">
											<div className="provider-metrics__discoverability-header">
												<span>
													Mentioned {providerCitation.mentioned_count || 0} /{" "}
													{providerCitation.queries_tested} queries
												</span>
												<span className="provider-metrics__discoverability-pct provider-metrics__discoverability-pct--mentioned">
													{Math.round((providerCitation.mention_rate || 0) * 100)}%
												</span>
											</div>
											<div className="provider-metrics__discoverability-track">
												<div
													className="provider-metrics__discoverability-fill provider-metrics__discoverability-fill--mentioned"
													style={{
														width: `${Math.round((providerCitation.mention_rate || 0) * 100)}%`,
													}}
												/>
											</div>
										</div>

										{providerCitation.avg_position && (
											<div className="provider-metrics__avg-position">
												Avg source position:{" "}
												<span className="provider-metrics__avg-position-value">
													#{providerCitation.avg_position}
												</span>
											</div>
										)}
									</div>
								</div>
							)}

							{allExpanded && (
								<div className="provider-metrics__queries-panel">
									{metric.query_results && metric.query_results.length > 0 ? (
										<div className="provider-metrics__queries-list">
											{metric.query_results.map((qr, idx) => (
												<QueryResultRow key={idx} queryResult={qr} />
											))}
										</div>
									) : (
										<p className="provider-metrics__queries-empty">
											Query detail not available for this audit
										</p>
									)}
								</div>
							)}
						</motion.div>
					);
				})}
			</div>
		</motion.div>
	);
}
